import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import _range from 'lodash/range';
import { Suspense } from 'react';
import { FormattedMessage } from 'react-intl';

import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { queryKeys } from '@/query-keys';

import { ChannelCard } from './channel-card';
import { useChannelDescription } from './use-channel-description';

export function ChannelsContent() {
  const { selectedOpeningIds, recommendedChannelSlugs } =
    useJobPostingContext();
  const { getDescription } = useChannelDescription();

  const {
    data: { channels, indeed_integration_enabled: indeedIntegrationEnabled },
  } = useSuspenseQuery({
    queryKey: [...queryKeys.jobPosting.channels, selectedOpeningIds],
    queryFn: () =>
      SourcingService.getInternalApiSourcingSourcingChannels(
        selectedOpeningIds,
      ),
  });

  if (channels.length === 0) {
    return (
      <div className="flex w-full flex-col items-start justify-center gap-4 p-4">
        <Typography variant="body1">
          <FormattedMessage
            defaultMessage="Looks like there are no publishable channels configured for this account at this time."
            description="Message to show when there are no publishable channels configured for the account"
          />
        </Typography>
      </div>
    );
  }

  const recommendedChannels = channels.filter(channel =>
    recommendedChannelSlugs.includes(channel.slug),
  );
  const otherChannels = channels.filter(
    channel =>
      !!getDescription(channel.slug) &&
      !recommendedChannels.some(
        recommendedChannel => recommendedChannel.slug === channel.slug,
      ),
  );

  return (
    <>
      {/* Recommended channels */}
      {recommendedChannels.length > 0 && (
        <>
          <Typography variant="caption">
            <FormattedMessage
              defaultMessage="Recommended Channels"
              description="Title for the recommended channels section"
            />
          </Typography>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
            {recommendedChannels.map(channel => (
              <ChannelCard
                key={channel.slug}
                channel={channel}
                indeedIntegrationEnabled={indeedIntegrationEnabled}
              />
            ))}
          </div>
          <Typography
            variant="caption"
            className="text-fx-neutral-900-base text-[10px] font-medium uppercase"
          >
            <FormattedMessage
              defaultMessage="Other Channels"
              description="Title for the other channels section"
            />
          </Typography>
        </>
      )}

      {/* All other channels */}
      <div className="grid grid-cols-2 gap-x-8 gap-y-4">
        {otherChannels.map(channel => (
          <ChannelCard
            key={channel.slug}
            channel={channel}
            indeedIntegrationEnabled={indeedIntegrationEnabled}
          />
        ))}
      </div>
    </>
  );
}

export function Channels() {
  return (
    <Suspense fallback={<Channels.Skeleton />}>
      <ChannelsContent />
    </Suspense>
  );
}

Channels.Skeleton = function ChannelsSkeleton() {
  const { recommendedChannelSlugs } = useJobPostingContext();

  /* Get the number of channels from the cached data, so that with subsequent render the skeleton count is the same and does not jump */
  const queryClient = useQueryClient();
  const cachedData = queryClient.getQueryData<{
    channels: unknown[];
  }>(['channels']);
  const skeletonCount = cachedData?.channels.length ?? 4;

  if (recommendedChannelSlugs.length > 0) {
    return (
      <>
        <Typography variant="caption">
          <FormattedMessage
            defaultMessage="Recommended Channels"
            description="Title for the recommended channels section"
          />
        </Typography>
        <div className="grid grid-cols-2 gap-x-8 gap-y-4">
          {_range(4).map(index => (
            <div
              key={index}
              className="w-90 flex items-center gap-4 rounded-lg border p-4"
            >
              <Checkbox checked={false} disabled />
              <div className="flex w-full flex-col items-start justify-center gap-4"></div>
            </div>
          ))}
        </div>
        <Typography
          variant="caption"
          className="text-fx-neutral-900-base text-[10px] font-medium uppercase"
        >
          <FormattedMessage
            defaultMessage="Other Channels"
            description="Title for the other channels section"
          />
        </Typography>
        <div className="grid grid-cols-2 gap-x-8 gap-y-4">
          {_range(4).map(index => (
            <div
              key={index}
              className="w-90 flex items-center gap-4 rounded-lg border p-4"
            >
              <Checkbox checked={false} disabled />
              <div className="flex w-full flex-col items-start justify-center gap-4"></div>
            </div>
          ))}
        </div>
      </>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-x-8 gap-y-4">
      {_range(skeletonCount).map(index => (
        <div
          key={index}
          className="border-fx-neutral-200 w-90 flex items-center gap-4 rounded-lg border p-4"
        >
          <Checkbox checked={false} disabled />
          <div className="flex w-full flex-col items-start justify-center gap-4">
            <Skeleton className="h-[25px] w-32" />
            <Skeleton className="h-5 w-40" />
            <Skeleton className="-mt-2 h-4 w-full" />
            <Skeleton className="-mt-3 h-4 w-[90%]" />
          </div>
        </div>
      ))}
    </div>
  );
};
